import { httpGet } from 'api/api'
import { selectuserInfo } from 'hooks/userInfo'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import ContentPasteSearchTwoToneIcon from '@mui/icons-material/ContentPasteSearchTwoTone'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import ReceiptIcon from '@mui/icons-material/Receipt'
import {
	Stack,
	Grid,
	Paper,
	Box,
	Button,
	TextField,
	FormControl,
	InputLabel,
	Typography,
	Select,
	useMediaQuery,
	MenuItem,
	Breadcrumbs,
	FormControlLabel,
	Radio,
	RadioGroup,
	useTheme,
	styled,
} from '@mui/material'
import LinearProgress from '@mui/material/LinearProgress'
import { PageBg } from 'components/ComponentStyles'
import {
	DataGridPremium,
	koKR,
	GridColDef,
	GridSelectionModel,
	GridDensity,
	GridRowHeightParams,
	useGridApiRef,
	GridColumnVisibilityModel,
} from '@mui/x-data-grid-premium'
import { getSettings } from 'helpers/storage'
import XLSX from 'xlsx-js-style'
import { alertModal, compareDate, hexToRgba, isValidDateType } from 'util/util'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/ko'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { SearchRowItem } from 'components/FrameworkStyle'
import ClientSearchPopup from './clientSearchPopup'

const locales = ['ko'] as const

const PageBg2 = styled('div')(({ theme }) => ({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	padding: '1rem',
	borderRadius: '0.5rem',
	background: `${hexToRgba(theme.palette.custom.wrapper, 1)}`,
	// filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
	[`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
		flexDirection: 'column',
		borderRadius: 0,
	},
	// backdropFilter: 'blur(10px)',
}))

const PageWrap = styled('div')(({ theme }) => ({
	marginBottom: '1rem',
	position: 'relative',
	top: '0',
	left: '0',
	right: '0',
	zIndex: '110',
	[`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
		//marginLeft: '-1.5rem',
		//marginRight: '-1.5rem',
		top: '0',
		//borderRadius: '0',
	},
}))

const BtnBox = styled(Box)(({ theme }) => ({
	// [`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
	// 	marginTop: '1rem',
	// },
}))

const TitleText = styled(Typography)(({ theme }) => ({
	[`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
		display: 'none',
	},
}))

type requestType = {
	clientID: number
	startDate: string
	endDate: string
	patientName: string
	chartNo: string
	receptionRegNum: string
	searchType: string
	socialNumber: string
}

const initialRequest: requestType = {
	clientID: 7666,
	startDate: moment(new Date()).isBefore('2023-08-01')
		? moment('2023-08-01').format('yyyy-MM-DD')
		: moment().subtract(1, 'days').format('YYYY-MM-DD'), // 시작일자
	endDate: moment().format('YYYY-MM-DD'), // 종료일자
	patientName: '',
	chartNo: '',
	receptionRegNum: '',
	searchType: 'R',
	socialNumber: '',
}

const formatHeaderName = (label: string) => {
	return label.includes('(') ? label.replace(/\s*\(/, '\n(') : label
}

const formatDiseaseName = (value: unknown) => {
	if (typeof value !== 'string') {
		return value ?? ''
	}

	return value
		.split(',')
		.map((item) => item.trim())
		.filter(Boolean)
		.join('\n')
}

const getDiseaseNameDisplayText = (row: any, value: unknown) => {
	let text =
		typeof value === 'string'
			? value
			: value === undefined || value === null
				? ''
				: String(value)

	const rprValue = row?.['RPR 정성']
	const hivValue = row?.['AIDS(HIV) Ag/Ab(combo)']
	const isRprReactive =
		typeof rprValue === 'string' &&
		(rprValue.toLowerCase() === 'reactive' || rprValue === '양성')
	const isHivLater = hivValue === '추후보고'

	if (isRprReactive) text += text === '' ? '매독' : ' / 매독'
	if (isHivLater) text += text === '' ? '추후보고' : ' / 추후보고'

	return text
}

const getCellDisplayValue = (field: string, row: any, forExcel = false) => {
	if (field === 'DiseaseName') {
		const diseaseText = getDiseaseNameDisplayText(row, row?.[field])
		return forExcel ? formatDiseaseName(diseaseText) : diseaseText
	}

	return row?.[field] ?? ''
}

const MIN_GRID_ROW_HEIGHT = 44
const GRID_ROW_LINE_HEIGHT = 18
const GRID_ROW_VERTICAL_PADDING = 12
const EXCEL_MIN_WCH = 4
const EXCEL_MAX_WCH = 24
const EXCEL_ROW_HEIGHT_SCALE = 3

const toExcelWchFromGridWidth = (width?: number) => {
	const fallbackWidth = 80
	const gridWidth = typeof width === 'number' ? width : fallbackWidth

	return Math.max(
		EXCEL_MIN_WCH,
		Math.min(EXCEL_MAX_WCH, Math.round(gridWidth / 8)),
	)
}

const estimateExcelLineCount = (value: unknown, wch: number) => {
	const safeWch = Math.max(1, wch)
	const text = String(value ?? '')
	const logicalLines = text.split('\n')

	const wrappedLineCount = logicalLines.reduce((acc, line) => {
		const lineLength = line.length || 1
		return acc + Math.max(1, Math.ceil(lineLength / safeWch))
	}, 0)

	return Math.max(1, wrappedLineCount)
}

const getAutoRowHeight = (params: GridRowHeightParams) => {
	const diseaseText = formatDiseaseName(params.model?.DiseaseName)
	const lineCount = String(diseaseText).split('\n').filter(Boolean).length || 1

	return Math.max(
		MIN_GRID_ROW_HEIGHT,
		lineCount * GRID_ROW_LINE_HEIGHT + GRID_ROW_VERTICAL_PADDING,
	)
}

const gridColumns: GridColDef[] = [
	{
		field: 'rowNum',
		headerName: 'No',
		type: 'string',
		width: 50,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
	},
	{
		field: 'ChartNo',
		headerName: '수번',
		type: 'string',
		width: 100,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
	},
	{
		field: 'SocialNumber',
		headerName: '생년월일',
		type: 'string',
		width: 100,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
	},
	{
		field: 'PatientAge',
		headerName: '나이',
		type: 'string',
		width: 50,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
	},
	{
		field: 'Gender',
		headerName: '성별',
		type: 'string',
		width: 50,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
	},
	{
		field: 'PatientName',
		headerName: '수진자명',
		type: 'string',
		width: 100,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
	},
	{
		field: 'Hemoglobin(Hgb)',
		headerName: formatHeaderName('HB (Hemoglobin)'),
		type: 'string',
		width: 120,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
		renderCell: (params) => {
			const { Decision_Hgb } = params.row
			const isBold = Decision_Hgb === 'H' || Decision_Hgb === 'L'
			const textColor =
				Decision_Hgb === 'H' ? 'red' : Decision_Hgb === 'L' ? 'blue' : 'inherit'
			return (
				<span
					style={{
						fontWeight: isBold ? 'bold' : 'normal',
						color: textColor,
					}}
				>
					{params.value}
				</span>
			)
		},
	},
	{
		field: 'Decision_Hgb',
		headerName: '판정',
		type: 'string',
		width: 40,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
		renderCell: (params) => {
			const value = params.value as string | undefined
			const isBold = value === 'H' || value === 'L'
			const textColor = value === 'H' ? 'red' : value === 'L' ? 'blue' : 'inherit'
			return (
				<span
					style={{
						fontWeight: isBold ? 'bold' : 'normal',
						color: textColor,
					}}
				>
					{value}
				</span>
			)
		},
	},
	{
		field: 'AST(sGOT)',
		headerName: formatHeaderName('AST (SGOT)'),
		type: 'string',
		width: 80,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
		renderCell: (params) => {
			const { Decision_AST } = params.row
			const isBold = Decision_AST === 'H' || Decision_AST === 'L'
			const textColor =
				Decision_AST === 'H' ? 'red' : Decision_AST === 'L' ? 'blue' : 'inherit'
			return (
				<span
					style={{
						fontWeight: isBold ? 'bold' : 'normal',
						color: textColor,
					}}
				>
					{params.value}
				</span>
			)
		},
	},
	{
		field: 'Decision_AST',
		headerName: '판정',
		type: 'string',
		width: 40,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
		renderCell: (params) => {
			const value = params.value as string | undefined
			const textColor = value === 'H' ? 'red' : value === 'L' ? 'blue' : 'inherit'
			const isBold = value === 'H'
			return (
				<span
					style={{
						fontWeight: isBold ? 'bold' : 'normal',
						color: textColor,
					}}
				>
					{value}
				</span>
			)
		},
	},
	{
		field: 'ALT(sGPT)',
		headerName: formatHeaderName('ALT (SGPT)'),
		type: 'string',
		width: 80,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
		renderCell: (params) => {
			const { Decision_ALT } = params.row
			const isBold = Decision_ALT === 'H' || Decision_ALT === 'L'
			const textColor =
				Decision_ALT === 'H' ? 'red' : Decision_ALT === 'L' ? 'blue' : 'inherit'
			return (
				<span
					style={{
						fontWeight: isBold ? 'bold' : 'normal',
						color: textColor,
					}}
				>
					{params.value}
				</span>
			)
		},
	},
	{
		field: 'Decision_ALT',
		headerName: '판정',
		type: 'string',
		width: 40,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
		renderCell: (params) => {
			const value = params.value as string | undefined
			const isBold = value === 'H' || value === 'L'
			const textColor = value === 'H' ? 'red' : value === 'L' ? 'blue' : 'inherit'
			return (
				<span
					style={{
						fontWeight: isBold ? 'bold' : 'normal',
						color: textColor,
					}}
				>
					{value}
				</span>
			)
		},
	},
	{
		field: 'r-GTP',
		headerName: 'γ-GTP',
		type: 'string',
		width: 80,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
		renderCell: (params) => {
			const { Decision_rGTP } = params.row
			const isBold = Decision_rGTP === 'H' || Decision_rGTP === 'L'
			const textColor =
				Decision_rGTP === 'H' ? 'red' : Decision_rGTP === 'L' ? 'blue' : 'inherit'
			return (
				<span
					style={{
						fontWeight: isBold ? 'bold' : 'normal',
						color: textColor,
					}}
				>
					{params.value}
				</span>
			)
		},
	},
	{
		field: 'Decision_rGTP',
		headerName: '판정',
		type: 'string',
		width: 40,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
		renderCell: (params) => {
			const value = params.value as string | undefined
			const isBold = value === 'H' || value === 'L'
			const textColor = value === 'H' ? 'red' : value === 'L' ? 'blue' : 'inherit'
			return (
				<span
					style={{
						fontWeight: isBold ? 'bold' : 'normal',
						color: textColor,
					}}
				>
					{value}
				</span>
			)
		},
	},
	{
		field: 'Cholesterol,total',
		headerName: 'Cholesterol\nTotal',
		type: 'string',
		width: 110,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
		renderCell: (params) => {
			const { Decision_Cholesterol } = params.row
			const isBold = Decision_Cholesterol === 'H' || Decision_Cholesterol === 'L'
			const textColor =
				Decision_Cholesterol === 'H'
					? 'red'
					: Decision_Cholesterol === 'L'
						? 'blue'
						: 'inherit'
			return (
				<span
					style={{
						fontWeight: isBold ? 'bold' : 'normal',
						color: textColor,
					}}
				>
					{params.value}
				</span>
			)
		},
	},
	{
		field: 'Decision_Cholesterol',
		headerName: '판정',
		type: 'string',
		width: 40,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
		renderCell: (params) => {
			const value = params.value as string | undefined
			const isBold = value === 'H' || value === 'L'
			const textColor = value === 'H' ? 'red' : value === 'L' ? 'blue' : 'inherit'
			return (
				<span
					style={{
						fontWeight: isBold ? 'bold' : 'normal',
						color: textColor,
					}}
				>
					{value}
				</span>
			)
		},
	},
	{
		field: 'Glucose',
		headerName: 'Glocose',
		type: 'string',
		width: 80,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
		renderCell: (params) => {
			const { Decision_Glucose } = params.row
			const isBold = Decision_Glucose === 'H' || Decision_Glucose === 'L'
			const textColor =
				Decision_Glucose === 'H'
					? 'red'
					: Decision_Glucose === 'L'
						? 'blue'
						: 'inherit'
			return (
				<span
					style={{
						fontWeight: isBold ? 'bold' : 'normal',
						color: textColor,
					}}
				>
					{params.value}
				</span>
			)
		},
	},
	{
		field: 'Decision_Glucose',
		headerName: '판정',
		type: 'string',
		width: 40,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
		renderCell: (params) => {
			const value = params.value as string | undefined
			const isBold = value === 'H' || value === 'L'
			const textColor = value === 'H' ? 'red' : value === 'L' ? 'blue' : 'inherit'
			return (
				<span
					style={{
						fontWeight: isBold ? 'bold' : 'normal',
						color: textColor,
					}}
				>
					{value}
				</span>
			)
		},
	},
	{
		field: 'RPR 정성',
		headerName: 'RPR\n(VDRL-정성)',
		type: 'string',
		width: 100,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
		renderCell: (params) => {
			const value = params.value as string | undefined
			const isBold = value === '양성'
			return (
				<span
					style={{
						fontWeight: isBold ? 'bold' : 'normal',
						color: isBold ? 'red' : 'inherit',
					}}
				>
					{value}
				</span>
			)
		},
	},
	{
		field: 'AIDS(HIV) Ag/Ab(combo)',
		headerName: 'HIV\nAg/Ab\n(Combo)',
		type: 'string',
		width: 100,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
		renderCell: (params) => {
			const value = params.value as string | undefined
			const isBold = value !== '음성'

			return (
				<span
					style={{
						fontWeight: isBold ? 'bold' : 'normal',
						color: isBold ? 'red' : '',
					}}
				>
					{value}
				</span>
			)
		},
	},
	{
		field: 'DiseaseName',
		headerName: '소견',
		type: 'string',
		width: 200,
		editable: false,
		disableColumnMenu: true,
		headerAlign: 'center',
		align: 'center',
		// valueFormatter: (value) => formatDiseaseName(value.value),
		cellClassName: 'disease-name-cell',
		renderCell: (params) => {
			const text = getDiseaseNameDisplayText(params.row, params.value)
			return <span>{text}</span>
		},
	},
]

const ResultTemp = () => {
	const theme = useTheme()
	const isMdUp = useMediaQuery(theme.breakpoints.up('md'))
	const isLgUp = useMediaQuery(theme.breakpoints.up('lg'))
	const locale = useState<(typeof locales)[number]>('ko')
	const userInfo = useSelector(selectuserInfo)
	const env = getSettings()
	const gridRef = useGridApiRef()
	const [page, setPage] = useState(0)
	const [size1, setSize1] = useState(env.row ? env.row : 100)
	const [gridIsLoading, setGridIsLoading] = useState(false)
	// const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([])
	const [columnVisibilityModel, setColumnVisibilityModel] =
		useState<GridColumnVisibilityModel>({
			rowNum: true,
			ChartNo: true,
			SocialNumber: true,
			PatientAge: true,
			Gender: true,
			PatientName: true,
			'Hemoglobin(Hgb)': true,
			Decision_Hgb: true,
			'AST(sGOT)': true,
			Decision_AST: true,
			'ALT(sGPT)': true,
			Decision_ALT: true,
			'r-GTP': true,
			Decision_rGTP: true,
			'Cholesterol,total': true,
			Decision_Cholesterol: true,
			Glucose: true,
			Decision_Glucose: true,
			'RPR 정성': true,
			'AIDS(HIV) Ag/Ab(combo)': true,
			DiseaseName: true,
		})

	const [search, setSearch] = useState<requestType>({
		...initialRequest,
		clientID: userInfo.infoType === 'C' ? userInfo.clientID : 7666, // 거래처id
	})

	const disabledReset = false
	const [disabledSearch, setDisabledSearch] = useState(false)
	const [dateRange, setDateRange] = useState('1d')

	const [data, setData] = useState<any[]>([])

	const buildCoverSheet = (workbook: any) => {
		const title = '신입수용자 혈액검사 결과표'
		const dateText = `${moment(search.startDate).format('YYYY년 MM월 DD일')} ~ ${moment(
			search.endDate,
		).format('YYYY년 MM월 DD일')}`
		const countText = `${data.length}명`

		const sheet = XLSX.utils.aoa_to_sheet([
			[],
			[],
			[],
			[],
			[],
			[],
			[],
			[],
			[],
			[],
			[],
			[],
			[],
			[],
			[],
			[],
			[],
			['', '', '', '', '', '', '', ''],
			['', '', '', '', '', '', '', ''],
			['', '', '', '', '', '', '', ''],
			['', '', '', '', '', '', '', ''],
			['', '', '', '', '', '', '', ''],
			['', '', '', '', '', '', '', ''],
			['', '', '', '', '', '', '', ''],
			['', '', '', '', '', '', '', ''],
		])

		XLSX.utils.sheet_add_aoa(
			sheet,
			[
				[],
				[],
				[],
				[],
				[],
				[],
				[],
				[],
				[],
				[],
				[{ v: title, t: 's' }],
				[],
				[],
				['', '', { v: `검사일자 : ${dateText}`, t: 's' }],
				['', '', { v: `검사인원 : ${countText}`, t: 's' }],
			],
			{ origin: 'A1' },
		)

		sheet['A11'].s = {
			font: { name: 'Arial', sz: 22, bold: true, color: { rgb: 'ff1f1f1f' } },
			alignment: { horizontal: 'center', vertical: 'center' },
		}
		sheet['C14'].s = {
			font: { name: 'Arial', sz: 14, bold: false, color: { rgb: 'ff1f1f1f' } },
			alignment: { horizontal: 'left', vertical: 'center' },
		}
		sheet['C15'].s = {
			font: { name: 'Arial', sz: 14, bold: false, color: { rgb: 'ff1f1f1f' } },
			alignment: { horizontal: 'left', vertical: 'center' },
		}

		sheet['!merges'] = [
			{ s: { r: 10, c: 0 }, e: { r: 10, c: 7 } },
			{ s: { r: 13, c: 2 }, e: { r: 13, c: 7 } },
			{ s: { r: 14, c: 2 }, e: { r: 14, c: 7 } },
		]

		sheet['!pageSetup'] = {
			paperSize: 9,
			orientation: 'landscape',
			fitToPage: true,
			fitToHeight: 1,
			fitToWidth: 1,
			horizontalCentered: true,
			verticalCentered: true,
		}

		sheet['!ref'] = 'A1:H25'

		sheet['!margins'] = {
			left: 0.3,
			right: 0.3,
			top: 0.4,
			bottom: 0.4,
			header: 0.2,
			footer: 0.2,
		}

		sheet['!cols'] = [
			{ wch: 14 },
			{ wch: 15 },
			{ wch: 14 },
			{ wch: 14 },
			{ wch: 14 },
			{ wch: 14 },
			{ wch: 14 },
			{ wch: 14 },
		]

		sheet['!rows'] = [
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 36 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 24 },
			{ hpt: 24 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
			{ hpt: 18 },
		]

		XLSX.utils.book_append_sheet(workbook, sheet, '표지')
	}

	const buildDataSheet = (workbook: any) => {
		const dataSheetName = '검사결과'
		const titleText = `${moment(search.startDate).format('YYYY년 MM월 DD일')} ~ ${moment(
			search.endDate,
		).format('YYYY년 MM월 DD일')} 서울구치소 신입자 혈액검사 결과보고서`
		const excelColumnWidths = gridColumns.map((column) =>
			toExcelWchFromGridWidth(column.width),
		)
		const maxHeaderLineCount = Math.max(
			...gridColumns.map(
				(column) =>
					String(column.headerName ?? column.field)
						.split('\n')
						.filter(Boolean).length,
			),
		)
		const headerStyle = {
			font: { name: 'Arial', sz: 13, bold: true, color: { rgb: 'ff1f1f1f' } },
			fill: { patternType: 'solid', fgColor: { rgb: 'ffe9e9e9' } },
			border: {
				top: { style: 'thin', color: { rgb: 'ff000000' } },
				left: { style: 'thin', color: { rgb: 'ff000000' } },
				bottom: { style: 'thin', color: { rgb: 'ff000000' } },
				right: { style: 'thin', color: { rgb: 'ff000000' } },
			},
			alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
		}
		const dataStyle = {
			font: { name: 'Arial', sz: 14, color: { rgb: 'ff1f1f1f' } },
			border: {
				top: { style: 'thin', color: { rgb: 'ff000000' } },
				left: { style: 'thin', color: { rgb: 'ff000000' } },
				bottom: { style: 'thin', color: { rgb: 'ff000000' } },
				right: { style: 'thin', color: { rgb: 'ff000000' } },
			},
			alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
		}
		const diseaseDataStyle = {
			...dataStyle,
			alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
		}
		const getDecisionTextStyle = (decisionValue: unknown) => {
			if (decisionValue === 'H') {
				return { bold: true, colorRgb: 'ffff0000' }
			}

			if (decisionValue === 'L') {
				return { bold: true, colorRgb: 'ff0000ff' }
			}

			return { bold: false, colorRgb: 'ff1f1f1f' }
		}

		const getExcelDataStyle = (
			field: string,
			row: any,
			rawValue: unknown,
			baseStyle: any,
		) => {
			let textStyle = { bold: false, colorRgb: 'ff1f1f1f' }

			switch (field) {
				case 'Hemoglobin(Hgb)':
					textStyle = getDecisionTextStyle(row?.Decision_Hgb)
					break
				case 'Decision_Hgb':
					textStyle = getDecisionTextStyle(rawValue)
					break
				case 'AST(sGOT)':
					textStyle = getDecisionTextStyle(row?.Decision_AST)
					break
				case 'Decision_AST': {
					const decisionStyle = getDecisionTextStyle(rawValue)
					textStyle = {
						bold: rawValue === 'H',
						colorRgb: decisionStyle.colorRgb,
					}
					break
				}
				case 'ALT(sGPT)':
					textStyle = getDecisionTextStyle(row?.Decision_ALT)
					break
				case 'Decision_ALT':
					textStyle = getDecisionTextStyle(rawValue)
					break
				case 'r-GTP':
					textStyle = getDecisionTextStyle(row?.Decision_rGTP)
					break
				case 'Decision_rGTP':
					textStyle = getDecisionTextStyle(rawValue)
					break
				case 'Cholesterol,total':
					textStyle = getDecisionTextStyle(row?.Decision_Cholesterol)
					break
				case 'Decision_Cholesterol':
					textStyle = getDecisionTextStyle(rawValue)
					break
				case 'Glucose':
					textStyle = getDecisionTextStyle(row?.Decision_Glucose)
					break
				case 'Decision_Glucose':
					textStyle = getDecisionTextStyle(rawValue)
					break
				case 'RPR 정성':
					textStyle = {
						bold: rawValue === '양성',
						colorRgb: rawValue === '양성' ? 'ffff0000' : 'ff1f1f1f',
					}
					break
				case 'AIDS(HIV) Ag/Ab(combo)':
					textStyle = {
						bold: rawValue !== '음성',
						colorRgb: rawValue !== '음성' ? 'ffff0000' : 'ff1f1f1f',
					}
					break
				default:
					break
			}

			return {
				...baseStyle,
				font: {
					...baseStyle.font,
					bold: textStyle.bold,
					color: { rgb: textStyle.colorRgb },
				},
			}
		}
		const titleStyle = {
			font: { name: 'Arial', sz: 20, bold: true, color: { rgb: 'ff1f1f1f' } },
			alignment: { horizontal: 'center', vertical: 'center' },
		}

		const rows = [
			[{ v: titleText, t: 's', s: titleStyle }],
			gridColumns.map((column) => ({
				v: column.headerName,
				t: 's',
				s: headerStyle,
			})),
			...data.map((row) =>
				gridColumns.map((column) => {
					const cellValue = getCellDisplayValue(column.field, row, true)
					const baseStyle =
						column.field === 'DiseaseName' ? diseaseDataStyle : dataStyle

					return {
						v: cellValue,
						t: 's',
						s: getExcelDataStyle(column.field, row, cellValue, baseStyle),
					}
				}),
			),
		]

		const sheet = XLSX.utils.aoa_to_sheet(rows)
		sheet['!merges'] = [
			{
				s: { r: 0, c: 0 },
				e: { r: 0, c: gridColumns.length - 1 },
			},
		]
		sheet['!pageSetup'] = {
			paperSize: 9,
			orientation: 'landscape',
			fitToPage: true,
			fitToHeight: 0,
			fitToWidth: 1,
		}
		sheet['!margins'] = {
			left: 0.25,
			right: 0.25,
			top: 0.35,
			bottom: 0.35,
			header: 0.15,
			footer: 0.15,
		}
		sheet['!cols'] = excelColumnWidths.map((wch) => ({ wch }))
		sheet['!rows'] = [
			{ hpt: 24 * EXCEL_ROW_HEIGHT_SCALE },
			{ hpt: Math.max(26, maxHeaderLineCount * 16) },
			...data.map((row) => {
				const maxLineCountInRow = gridColumns.reduce(
					(maxLineCount, column, index) => {
						const rawValue = getCellDisplayValue(column.field, row, true)
						const lineCount = estimateExcelLineCount(
							rawValue,
							excelColumnWidths[index],
						)

						return Math.max(maxLineCount, lineCount)
					},
					1,
				)

				return {
					hpt: Math.max(20, maxLineCountInRow * 18) * EXCEL_ROW_HEIGHT_SCALE,
				}
			}),
		]

		const s2CellAddress = XLSX.utils.encode_cell({ r: 1, c: 18 })
		if (sheet[s2CellAddress]) {
			sheet[s2CellAddress].s = {
				...(sheet[s2CellAddress].s ?? headerStyle),
				font: {
					...(sheet[s2CellAddress].s?.font ?? headerStyle.font),
					sz: 12,
				},
			}
		}

		XLSX.utils.book_append_sheet(workbook, sheet, dataSheetName)

		const dataSheetIndex = workbook.SheetNames.indexOf(dataSheetName)
		const escapedSheetName = dataSheetName.replace(/'/g, "''")

		workbook.Workbook = workbook.Workbook ?? {}
		workbook.Workbook.Names = (workbook.Workbook.Names ?? []).filter(
			(name: any) =>
				!(name?.Name === '_xlnm.Print_Titles' && name?.Sheet === dataSheetIndex),
		)
		workbook.Workbook.Names.push({
			Name: '_xlnm.Print_Titles',
			Ref: `'${escapedSheetName}'!$2:$2`,
			Sheet: dataSheetIndex,
		})
	}

	const fetchData = () => {
		console.log('Request:', search)
		setGridIsLoading(true)
		httpGet('/test/receptionSelectTemp', search)
			.then((res) => {
				console.log(res)
				setData(res)
			})
			.finally(() => {
				setGridIsLoading(false)
			})
	}

	useEffect(() => {
		if (userInfo.infoType === 'C') fetchData()
	}, [])

	const excelDownload = () => {
		if (data.length === 0) {
			window.alert('엑셀로 출력할 데이터가 없습니다.')
			return
		}

		const workbook = XLSX.utils.book_new()
		buildCoverSheet(workbook)
		buildDataSheet(workbook)

		const title = '서울구치소 신입자 혈액검사 결과보고서'
		const dateText = `${moment(search.startDate).format('YYYY년 MM월 DD일')} ~ ${moment(
			search.endDate,
		).format('YYYY년 MM월 DD일')}`

		XLSX.writeFile(workbook, `${dateText} ${title}.xlsx`, {
			bookType: 'xlsx',
			cellStyles: true,
		})
	}

	// 내부사용자일 경우 거래처 선택 팝업
	const [clientDialogOpen, setClientDialogOpen] = useState(false)
	const [searchClientName, setSearchClientName] = useState('')
	const clientDialogClose = () => {
		setClientDialogOpen(false)
	}
	const clientPopupOpenAction = () => {
		setClientDialogOpen(true)
	}
	const clientPopupSubmit = (e: any) => {
		console.log(e)
		setSearchClientName(e.ClientName)
		setSearch({
			...search,
			clientID: e.ClientID,
		})
		setClientDialogOpen(false)
	}

	const actionReset = () => {
		setSearch({
			...initialRequest,
			clientID: userInfo.infoType === 'C' ? userInfo.clientID : '', // 거래처id
		})
	}
	const actionSearch = () => {
		if (!disabledSearch) fetchData()
	}

	const onKeyUp = (e: any) => {
		if (e.keyCode === 13) actionSearch()
	}

	// calcaulate today before n days
	const getBeforeDate = (n: number) => {
		setSearch({
			...search,
			startDate: moment(search.endDate).subtract(n, 'days').format('YYYY-MM-DD'),
		})
	}

	const searchDateRanageOnChange = (event: any) => {
		switch (event.target.value) {
			case '1d':
				getBeforeDate(1)
				break
			case '3d':
				getBeforeDate(3)
				break
			case '7d':
				getBeforeDate(7)
				break
			case '14d':
				getBeforeDate(14)
				break
			default:
				getBeforeDate(7)
				break
		}
		setDateRange(event.target.value)
		setDisabledSearch(false)
	}

	// date picker on change event

	const dateRanageOnChange = (e: any, target: string) => {
		// setIsDateRangeChanged(false)
		if (moment(new Date(e)).isBefore('2023-08-01')) {
			alertModal('2023-08-01 이전 날짜는 조회할 수 없습니다.', 'error', () => {})
			setSearch({
				...search,
				[target]: moment(new Date(e)).format('yyyy-MM-DD'),
			})
			return
		} else {
			if (moment(new Date(e)).format('yyyy-MM-DD').length === 10) {
				if (!isValidDateType(e)) {
					alertModal('날짜 형식이 올바르지 않습니다.', 'error', () => {})
					setDisabledSearch(true)
					return
				}

				if (target === 'startDate') {
					let diffValue = compareDate(e, search.endDate)
					if (diffValue > 0) {
						alertModal('시작일이 종료일보다 큽니다.', 'error', () => {})
						setDisabledSearch(true)

						setSearch((prevState) => {
							return {
								...prevState,
								[target]: moment(new Date(e)).format('yyyy-MM-DD'),
							}
						})
						return
					}
					if (diffValue < -1825) {
						alertModal('검색기간은 5년을 초과할 수 없습니다.', 'error', () => {})
						setDisabledSearch(true)
						return
					}
				} else if (target === 'endDate') {
					let diffValue = compareDate(e, search.startDate)
					if (diffValue < 0) {
						alertModal('종료일이 시작일보다 작습니다.', 'error', () => {})
						setDisabledSearch(true)

						setSearch((prevState) => {
							return {
								...prevState,
								[target]: moment(new Date(e)).format('yyyy-MM-DD'),
							}
						})
						return
					}
					if (diffValue > 1825) {
						alertModal('검색기간은 1년을 초과할 수 없습니다.', 'error', () => {})
						setDisabledSearch(true)
						return
					}
				}

				setDisabledSearch(false)

				setSearch((prevState) => {
					return {
						...prevState,
						[target]: moment(new Date(e)).format('yyyy-MM-DD'),
					}
				})
			} else {
				setDisabledSearch(true)
			}
		}
	}

	return (
		<>
			<ClientSearchPopup
				dialogOpen={clientDialogOpen}
				dialogClose={clientDialogClose}
				popupSubmit={clientPopupSubmit}
			/>
			<Stack
				sx={{
					position: 'sticky',
					top: '50px',
					zIndex: 100,
					padding: '0.5rem',
					borderRadius: '0.5rem',
					backgroundColor:
						theme.palette.mode === 'dark' ? 'rgba(50, 48, 54, 1)' : '#fff',
					'@media (max-width: 600px)': {
						position: 'relative',
						paddingTop: '15px',
						top: '0px',
					},
				}}
			>
				<PageWrap style={{ marginBottom: '0.5rem' }}>
					<PageBg2 sx={{ padding: '0.3rem 0.3rem 0rem 0.5rem' }}>
						<Breadcrumbs
							separator={<NavigateNextIcon fontSize="small" />}
							aria-label="breadcrumb"
							sx={{
								'@media (max-width: 900px': {
									display: 'none',
								},
							}}
						>
							<TitleText
								key="3"
								color="text.primary"
								sx={{ fontWeight: '500', fontSize: 17 }}
							>
								검사결과조회 다운로드 (
								{userInfo.infoType === 'C' ? (
									<>{userInfo.clientName}</>
								) : (
									searchClientName
								)}
								)
							</TitleText>
						</Breadcrumbs>
						<BtnBox>
							<div>
								<span>
									<Button
										size="small"
										color="success"
										variant="contained"
										startIcon={isMdUp && <RotateLeftIcon />}
										sx={{
											marginRight: '0.5rem',
											padding: isMdUp ? '4px 10px' : '4px 6px',
											minWidth: isMdUp ? '64px' : '50px',
										}}
										disabled={disabledReset}
										onClick={actionReset}
									>
										초기화
									</Button>
								</span>
								<span>
									<Button
										color="primary"
										variant="contained"
										startIcon={<ContentPasteSearchTwoToneIcon />}
										onClick={actionSearch}
										size="small"
										disabled={disabledSearch}
										sx={{ marginRight: '0.5rem' }}
									>
										조회
									</Button>
								</span>
								<span>
									<Button
										color="secondary"
										size="small"
										variant="contained"
										startIcon={<ReceiptIcon />}
										sx={{ marginRight: '0.5rem' }}
										onClick={excelDownload}
										// disabled={isRptType0 ? true : false}
									>
										다운로드
									</Button>
								</span>
							</div>
						</BtnBox>
					</PageBg2>
				</PageWrap>
				<Box
					component="form"
					noValidate
					autoComplete="off"
					onKeyUp={onKeyUp}
					sx={{
						borderTop:
							theme.palette.mode === 'dark'
								? '1px solid rgba(81, 81, 81, 1)'
								: '1px solid #ccc',
						paddingTop: '1rem',
					}}
				>
					<LocalizationProvider
						dateAdapter={AdapterDayjs}
						adapterLocale={locale}
					>
						<SearchRowItem>
							<p className="labelText">조회조건</p>
							<Grid
								container
								spacing={1}
							>
								<Grid
									item
									xs={6}
									sm={3}
									md={2}
								>
									<FormControl fullWidth>
										<InputLabel id="searchType-select-label">기간 유형</InputLabel>
										<Select
											labelId="searchType-select-label"
											id="searchTypee-simple-select"
											value={search.searchType}
											label="기간 유형"
											onChange={(e) => {
												setSearch({
													...search,
													searchType: e.target.value,
												})
											}}
											size={'small'}
										>
											<MenuItem value={'R'}>접수일자</MenuItem>
											<MenuItem value={'D'}>보고일자</MenuItem>
										</Select>
									</FormControl>
								</Grid>
								<Grid
									item
									xs={6}
									sm={3}
									md={2}
								>
									<DatePicker
										label="시작일"
										value={search.startDate}
										mask={'____-__-__'}
										inputFormat={'YYYY-MM-DD'}
										onChange={(newValue) => {
											dateRanageOnChange(newValue, 'startDate')
										}}
										renderInput={(params) => (
											<TextField
												size="small"
												{...params}
												fullWidth
											/>
										)}
									/>
								</Grid>
								<Grid
									item
									xs={6}
									sm={3}
									md={2}
								>
									<DatePicker
										label="종료일"
										value={search.endDate}
										mask={'____-__-__'}
										inputFormat={'YYYY-MM-DD'}
										onChange={(newValue) => {
											dateRanageOnChange(newValue, 'endDate')
										}}
										renderInput={(params) => (
											<TextField
												size="small"
												{...params}
												fullWidth
											/>
										)}
									/>
								</Grid>
								<Grid
									item
									xs={6}
									sm={3}
									md={2}
								>
									{isLgUp ? (
										<>
											<FormControl sx={{ pt: 0.8 }}>
												<RadioGroup
													row
													aria-labelledby="date-controlled-radio-buttons-group"
													name="date-radio-buttons-group"
													value={dateRange}
													onChange={searchDateRanageOnChange}
													sx={{ ml: '0.5rem' }}
												>
													<FormControlLabel
														value="1d"
														control={<Radio sx={{ padding: '1px' }} />}
														label="1일"
														sx={{
															'& .MuiFormControlLabel-label': { fontSize: '12px' },
														}}
													/>
													<FormControlLabel
														value="3d"
														control={<Radio sx={{ padding: '1px' }} />}
														label="3일"
														sx={{
															'& .MuiFormControlLabel-label': { fontSize: '12px' },
														}}
													/>
													<FormControlLabel
														value="7d"
														control={<Radio sx={{ padding: '1px' }} />}
														label="7일"
														sx={{
															'& .MuiFormControlLabel-label': { fontSize: '12px' },
														}}
													/>
													<FormControlLabel
														value="14d"
														control={<Radio sx={{ padding: '1px' }} />}
														label="14일"
														sx={{
															'& .MuiFormControlLabel-label': { fontSize: '12px' },
														}}
													/>
												</RadioGroup>
											</FormControl>
										</>
									) : (
										<>
											<FormControl fullWidth>
												<InputLabel id="date-range-select-label">조회 기간</InputLabel>
												<Select
													labelId="date-range-select-label"
													id="date-range-simple-select"
													value={dateRange}
													label="조회 기간"
													onChange={searchDateRanageOnChange}
													size={'small'}
												>
													<MenuItem value={'0'}>선택하세요.</MenuItem>
													<MenuItem value={'1d'}>1일</MenuItem>
													<MenuItem value={'3d'}>3일</MenuItem>
													<MenuItem value={'7d'}>7일</MenuItem>
													<MenuItem value={'14d'}>14일</MenuItem>
												</Select>
											</FormControl>
										</>
									)}
								</Grid>
							</Grid>
						</SearchRowItem>
						<SearchRowItem>
							<Grid
								container
								spacing={1}
							>
								<Grid
									item
									xs={6}
									sm={3}
									md={2}
									lg={2}
								>
									<TextField
										label="차트번호"
										variant="outlined"
										size="small"
										type="text"
										fullWidth
										value={search.chartNo}
										onChange={(e) => {
											setSearch({
												...search,
												chartNo: e.target.value,
											})
										}}
									/>
								</Grid>
								<Grid
									item
									xs={6}
									sm={3}
									md={2}
									lg={2}
								>
									<TextField
										label="수진자명"
										variant="outlined"
										size="small"
										type="text"
										fullWidth
										value={search.patientName}
										onChange={(e) => {
											setSearch({
												...search,
												patientName: e.target.value,
											})
										}}
									/>
								</Grid>
								{(userInfo.infoType === 'S' || userInfo.infoType === 'W') && (
									<Grid
										item
										xs={6}
										sm={3}
										md={2}
									>
										<TextField
											label="거래처"
											variant="outlined"
											size="small"
											type="text"
											fullWidth
											aria-readonly={true}
											value={searchClientName}
											onClick={clientPopupOpenAction}
										/>
									</Grid>
								)}
							</Grid>
						</SearchRowItem>
					</LocalizationProvider>
				</Box>
			</Stack>
			<Paper
				id="back-to-top"
				elevation={0}
				sx={{
					width: '100%',
					// overflow: 'hidden',
					backgroundColor: 'transparent',
					// overflow: 'auto',
				}}
			>
				<Grid container>
					<Grid
						item
						xs={12}
					>
						<PageBg style={{ marginTop: '1rem' }}>
							<Typography
								variant="subtitle1"
								component="h3"
								sx={{ lineHeight: '1rem' }}
							>
								수진자 리스트
							</Typography>
							<Box
								sx={{
									marginTop: '1rem',
									height: 813,
									'@media (max-width: 1199px)': {
										height: '400px',
										overflow: 'auto',
									},
								}}
							>
								<DataGridPremium
									apiRef={gridRef}
									page={page}
									onPageChange={(newPage) => setPage(newPage)}
									rows={data}
									getRowId={(row) => row.rowNum}
									columns={gridColumns}
									headerHeight={96}
									getRowHeight={getAutoRowHeight}
									getEstimatedRowHeight={() => MIN_GRID_ROW_HEIGHT}
									pageSize={size1}
									onPageSizeChange={(newPageSize) => setSize1(newPageSize)}
									rowsPerPageOptions={[5, 10, 20, 50, 100, 500, 1000]}
									pagination
									autoHeight={false}
									// autoHeight={isLgUp ? true : false}
									localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
									components={{
										LoadingOverlay: LinearProgress,
									}}
									loading={gridIsLoading}
									density={(env.desnse as GridDensity) || 'compact'}
									sx={{
										'& .MuiDataGrid-columnHeaderTitleContainer': {
											fontSize: '13px',
											justifyContent: 'center',
											alignItems: 'center',
											height: '100%',
											overflow: 'visible',
										},
										'& .MuiDataGrid-columnHeaderTitleContainerContent': {
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											width: '100%',
											overflow: 'visible',
										},
										'& .MuiDataGrid-row.no-image-row:hover': {
											backgroundColor:
												theme.palette.mode === 'dark'
													? 'rgba(50, 48, 54, 1) !important'
													: '#fff !important',
										},
										'& .MuiDataGrid-row:hover': {
											backgroundColor: 'rgba(0,0,0, 0.1) !important',
										},
										'& .Mui-selected:hover': {
											backgroundColor: 'rgba(75, 123, 236, 0.08) !important',
										},
										'& .MuiDataGrid-cell': {
											fontSize: '13px !important',
											whiteSpace: 'normal',
											alignItems: 'center',
											paddingTop: '6px',
											paddingBottom: '6px',
											borderRight:
												theme.palette.mode === 'dark'
													? '1px solid rgba(81, 81, 81, 1)'
													: '1px solid rgba(224, 224, 224, 1)',
										},
										'& .Mui-disabled': {
											color: 'rgba(0, 0, 0, 0.1) !important',
										},
										'& .MuiDataGrid-columnHeaderTitle': {
											whiteSpace: 'pre-line',
											lineHeight: 1.3,
											textAlign: 'center',
											width: '100%',
											overflow: 'visible',
										},
										'& .MuiDataGrid-columnHeader': {
											paddingTop: '8px',
											paddingBottom: '8px',
										},
										'& .disease-name-cell': {
											whiteSpace: 'pre-line',
											lineHeight: 1.4,
											paddingTop: '8px',
											paddingBottom: '8px',
											justifyContent: 'flex-start',
											textAlign: 'left',
										},
										'& .disease-name-cell .MuiDataGrid-cellContent': {
											width: '100%',
											whiteSpace: 'pre-line',
											textAlign: 'left',
										},
									}}
									disableMultipleSelection={false}
									initialState={{
										columns: {
											columnVisibilityModel,
										},
									}}
									onColumnVisibilityModelChange={(newModel) => {
										console.log(newModel)
										setColumnVisibilityModel(newModel)
									}}
								/>
							</Box>
						</PageBg>
					</Grid>
				</Grid>
			</Paper>
		</>
	)
}

export default ResultTemp
